import { Icon } from "./Icon.tsx";
import { color as colorVar, cx, fontSize, lineHeight, type ColorToken, type FontToken } from "./tokens.ts";

export type InlineRun = {
  text: string;
  bold?: boolean;
  italic?: boolean;
  strike?: boolean;
  code?: boolean;
  link?: string;
  chip?: "user" | "agent" | "channel" | "broadcast" | "self" | "group";
  onPress?: () => void;
};

export type InlineTextProps = {
  runs: InlineRun[];
  variant?: FontToken;
  color?: ColorToken;
  lines?: number;
  selectable?: boolean;
};

function Run({ run }: { run: InlineRun }) {
  const cls = cx(run.bold && "b-run-bold", run.italic && "b-run-italic", run.strike && "b-run-strike", run.code && "b-run-code");
  if (run.chip !== undefined) {
    const content = (
      <>
        {run.chip === "agent" ? <Icon name="agent" size="icon.sm" /> : null}
        {run.text}
      </>
    );
    return run.onPress === undefined ? (
      <span className={cx("b-chip", `b-chip-${run.chip}`, cls)}>{content}</span>
    ) : (
      <button type="button" className={cx("b-chip", `b-chip-${run.chip}`, "b-chip-action", cls)} onClick={run.onPress}>
        {content}
      </button>
    );
  }
  if (run.link !== undefined) {
    return (
      <a className={cx("b-link", cls)} href={run.link} target="_blank" rel="noreferrer noopener">
        {run.text}
      </a>
    );
  }
  if (run.onPress !== undefined) {
    return (
      <button type="button" className={cx("b-run-action", cls)} onClick={run.onPress}>
        {run.text}
      </button>
    );
  }
  return cls === "" ? <>{run.text}</> : <span className={cls}>{run.text}</span>;
}

/** Render-only rich inline text (message bodies). Chips: rounded gray pills; agent chips get the agent glyph. */
export function InlineText({ runs, variant = "body", color = "text.primary", lines, selectable = true }: InlineTextProps) {
  return (
    <span
      className={cx("b-text", "b-inline", lines !== undefined && "b-clamp", selectable && "b-selectable")}
      style={{ fontSize: fontSize(variant), lineHeight: lineHeight(variant), color: colorVar(color), WebkitLineClamp: lines }}
    >
      {runs.map((r, i) => (
        <Run key={i} run={r} />
      ))}
    </span>
  );
}
